/**
 * Per-day merger — the single source of truth for what shows on
 * the calendar for a given day.
 *
 * Inputs:
 *   `customPicks`        — user-drawn slots (origin always custom).
 *   `generated`          — fresh pipeline output for the day.
 *   `autoFillRequested`  — did the user tick the day checkbox?
 *   `droppedIds`         — generated ids the user removed while
 *                          auto-fill was on (so they stay hidden).
 *
 * Output:
 *   `effectivePicks`     — the flat list FullCalendar will render
 *                          for the day. The ONLY list the UI reads.
 *
 * ## Rules (in order)
 *
 * 1. **Auto-fill first, deduped by dropped-ids.** When
 *    `autoFillRequested`, every generated slot whose id isn't in
 *    `droppedIds` is emitted with `origin='generated'`. When
 *    `autoFillRequested=false`, no generated slot is emitted.
 *
 * 2. **Custom slots next, with generated-wins dedupe.** If a custom
 *    slot shares `(start, end)` with a slot already emitted in step
 *    1, skip it — generated wins by identity. Off-grid customs
 *    (10:15 when algorithm has 10:00) keep their own key and survive
 *    as siblings.
 *
 * 3. **No inner state.** The function is pure and takes no moment of
 *    its own — trivially unit-testable with a frozen timezone.
 */
import type { SelfSlot } from '../types';

/**
 * Dedup key — ISO `start-end`. Our generated slots are already
 * emitted on the same 15-min grid as custom clicks (both go through
 * `dateClick` or the pipeline's `sliceInterval`) so the raw strings
 * collide cleanly. If DST / format drift ever causes false
 * negatives, swap to ms-epoch here — see `toMs.ts`.
 */
function keyOf(slot: SelfSlot): string {
  return `${slot.start}-${slot.end}`;
}

export interface MergePicksArgs {
  customPicks: SelfSlot[];
  generated: SelfSlot[];
  autoFillRequested: boolean;
  droppedIds: Set<string>;
}

export function mergePicks({
  customPicks,
  generated,
  autoFillRequested,
  droppedIds,
}: MergePicksArgs): SelfSlot[] {
  const out: SelfSlot[] = [];
  const seen = new Set<string>();

  // Pass 1: auto-filled generated slots (filtered by droppedIds).
  if (autoFillRequested) {
    for (const g of generated) {
      if (droppedIds.has(g.id)) continue;
      const key = keyOf(g);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(g);
    }
  }

  // Pass 2: customs not already emitted as a generated slot.
  for (const c of customPicks) {
    const key = keyOf(c);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }

  return out;
}
